import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { Box, TextField, Button, Typography, Container, Alert } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get user's custom claims (role and vendorId)
      //const idTokenResult = await userCredential.user.getIdTokenResult();
      const idTokenResult = await userCredential.user.getIdTokenResult(true);
      const role = idTokenResult.claims.role;
      const vendorId = idTokenResult.claims.vendorId;

      console.log('User role:', role, 'Vendor ID:', vendorId);

      // Route based on role
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else if (role === 'vendor') {
        if (!vendorId) {
          setError('Partner account not properly configured. Contact admin.');
          return;
        }
        navigate('/vendor/dashboard', { state: { vendorId } });
      } else {
        setError('No role assigned to this account. Contact admin.');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      alert(`Password reset email sent to ${resetEmail}. Check your inbox.`);
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{
        mt: { xs: 4, sm: 8 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            textAlign: 'center'
          }}
        >
          ARCS Admin Dashboard
        </Typography>

        {!showForgotPassword ? (
          <Box component="form" onSubmit={handleLogin} sx={{ mt: { xs: 2, sm: 3 }, width: '100%' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: { xs: 1.5, sm: 1 } }}
            >
              Login
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={() => setShowForgotPassword(true)}
                sx={{ textTransform: 'none' }}
              >
                Forgot Password?
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" component="span" color="textSecondary">
                New partner?{' '}
              </Typography>
              <RouterLink to="/signup" style={{ textDecoration: 'none' }}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  Create an account
                </Typography>
              </RouterLink>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: { xs: 2, sm: 3 }, width: '100%' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Reset Password
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Enter your email address and we'll send you a link to reset your password.
            </Typography>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              margin="normal"
              required
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleForgotPassword}
              sx={{ mt: 2, py: { xs: 1.5, sm: 1 } }}
            >
              Send Reset Link
            </Button>

            <Button
              fullWidth
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
                setResetEmail('');
              }}
              sx={{ mt: 1 }}
            >
              Back to Login
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
}